import React from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import axios from '../util/Api.js';
import iotcevLogo from '../assets/receipts/iotcev-logo.png';
import aisLogo from '../assets/receipts/ais-logo.png';
import '../styles/receipt.css';

const CLIENT_API_URL = process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_CLIENT_API_URL : window.CLIENT_API_URL;

export const ReceiptDocument = ({ data }) => (
  <>
    <header className="receipt-brand">
      <div className="receipt-iotcev"><img src={iotcevLogo} alt="IoTC EV" /></div>
      <p>Internet of Things Consulting <span>(IoTC) - EV Charging</span></p>
      <img className="receipt-ais" src={aisLogo} alt="Aeon Innovative Solutions (AIS)" />
    </header>
    <h1>Charging receipt</h1>
    <p className="receipt-muted">IoTC EV is a member of AIS.</p>
    <div className="receipt-summary">
      <div><span className="receipt-label">Receipt reference</span><strong>{data.reference}</strong></div>
      <div><span className="receipt-label">Session total {data.currency}</span><strong className="receipt-amount">{data.total_text}</strong></div>
    </div>
    <section aria-labelledby="receipt-details-title">
      <h2 id="receipt-details-title">Session details</h2>
      <dl className="receipt-details">
        {[['Start', data.start_text], ['End', data.end_text], ['Duration', data.duration_text], ['Energy delivered', data.kwh_text]].map(([label, value]) => (
          <div key={label}><dt className="receipt-label">{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
      <dl className="receipt-meta">
        {[['Location', data.location], ['Address', data.address], ['Station', data.station], ['EVSE', data.evse], ['Transaction ID', data.transaction_id]].filter(([, value]) => value).map(([label, value]) => (
          <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
    </section>
    <section aria-labelledby="receipt-charges-title">
      <h2 id="receipt-charges-title">Charge breakdown</h2>
      <table className="receipt-charges" aria-label="Charge breakdown">
        <thead><tr><th scope="col">Description</th><th scope="col">Quantity</th><th scope="col">Rate</th><th scope="col">Amount</th></tr></thead>
        <tbody>{data.lines.map((line, index) => (
          <tr key={`${line.label}-${index}`}>
            <th scope="row">{line.label}{line.period && <span className="receipt-period">{line.period}</span>}</th>
            <td data-label="Quantity">{line.quantity || '—'}</td>
            <td data-label="Rate">{line.rate || '—'}</td>
            <td data-label="Amount">{line.amount_text}</td>
          </tr>
        ))}</tbody>
      </table>
      <dl className="receipt-totals">
        {data.subtotal_text != null && <div><dt>Subtotal</dt><dd>{data.subtotal_text}</dd></div>}
        {data.tax_text != null && <div><dt>{data.tax_label}</dt><dd>{data.tax_text}</dd></div>}
        <div className="receipt-grand-total"><dt>Session total {data.currency}</dt><dd>{data.total_text}</dd></div>
        {/* Settled for less than the session total (capped at the card hold, or the hold released). */}
        {data.charged_text != null && <div className="receipt-charged"><dt>{data.charged_label}</dt><dd>{data.charged_text}</dd></div>}
      </dl>
      {data.notes.map((note, index) => <p className="receipt-note" key={index}>{note}</p>)}
    </section>
    <p className="receipt-thanks">Thank you for charging with IoTC EV.</p>
  </>
);

const Receipt = () => {
  const { sessionId } = useParams();
  const intl = useIntl();
  const [receiptData, setReceiptData] = React.useState(null);
  const [ocmfData, setOcmfData] = React.useState(null);
  const [email, setEmail] = React.useState('');
  const [emailSending, setEmailSending] = React.useState(false);
  const [emailStatus, setEmailStatus] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Charging receipt | IoTC EV';
    return () => { document.title = previousTitle; };
  }, []);

  React.useEffect(() => {
    let current = true;
    setReceiptData(null);
    setError(false);
    setOcmfData(null);
    setEmailStatus(null);
    axios.get(`receipts/${sessionId}`).then(({ data }) => {
      if (!current) return;
      if (!data.data?.document) throw new Error('Receipt unavailable');
          const transaction_data = data?.data?.session?.transaction_data;
          if (transaction_data) {
            try {
              transaction_data.forEach((transacionData) => {
                transacionData.sampled_value.forEach((sampledValue) => {
                  if (sampledValue.format === 'SignedData') {
                    const match = /(4f434d46{1}.*)/g.exec(sampledValue.value);
                    if (match) {
                      const result = [];
                      for (let i = 0; i < sampledValue.value.length; i += 2) {
                        result.push(String.fromCharCode(parseInt(sampledValue.value.substr(i, 2), 16)));
                      }
                      setOcmfData(result.join(''));
                    }
                  }
                });
              });
            } catch {
              const tdata = JSON.stringify(transaction_data);
              const rx = /("sampled_value": \[{"value": ")(OCMF\|{1}.*\|{1}.*{"SD":"{1}.*"})(",)/g;
              const match = rx.exec(tdata);
              if (match) setOcmfData(match[2]);
            }
          }
      setEmail(data.data.customer_email || '');
      setReceiptData(data.data);
    }).catch(() => { if (current) setError(true); });
    return () => { current = false; };
  }, [sessionId, attempt]);

  const downloadOcmfFile = () => {
    const element = document.createElement('a');
    const url = URL.createObjectURL(new Blob([ocmfData], { type: 'text/plain' }));
    element.href = url;
    element.download = `ocmf_${receiptData.id}.txt`;
    document.body.appendChild(element);
    element.click();
    element.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const sendEmail = (event) => {
    event.preventDefault();
    if (!email || emailSending) return;
    setEmailSending(true);
    setEmailStatus(null);
    axios.post(`receipts/${sessionId}/email`, { email })
      .then(() => setEmailStatus('sent'))
      .catch(() => setEmailStatus('error'))
      .finally(() => setEmailSending(false));
  };

  return (
    <main className="receipt-page">
      <article className="receipt-sheet" aria-label="Charging receipt">
        {!receiptData ? (
          <div className="receipt-loading" role={error ? 'alert' : 'status'}>
            <h1>{error ? 'Receipt unavailable' : 'Preparing your receipt…'}</h1>
            {error && <><p>We could not load this receipt. Please try again.</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></>}
          </div>
        ) : (
          <>
            <ReceiptDocument data={receiptData.document} />
            <section className="receipt-actions" aria-label="Save your receipt">
              <a className="receipt-download" href={`${(CLIENT_API_URL || '/api').replace(/\/$/, '')}/receipts/${sessionId}/pdf`} target="_blank" rel="noopener noreferrer">
                <i className="ri-file-pdf-2-line" aria-hidden="true" />{intl.formatMessage({ id: 'receipt.downloadpdf' })}
              </a>
              <form onSubmit={sendEmail}>
                <label htmlFor="receipt-email">Email a copy of your receipt</label>
                <div className="receipt-email-row">
                  <input id="receipt-email" type="email" autoComplete="email" required value={email} onChange={event => { setEmail(event.target.value); setEmailStatus(null); }} placeholder={intl.formatMessage({ id: 'receipt.email.placeholder' })} />
                  <button type="submit" disabled={emailSending || !email}>{emailSending ? 'Sending…' : 'Send receipt'}</button>
                </div>
                {emailStatus && <p role="status" className={emailStatus === 'error' ? 'receipt-error' : 'receipt-success'}>{intl.formatMessage({ id: `receipt.email.${emailStatus}` })}</p>}
              </form>
              {ocmfData && <button type="button" className="receipt-ocmf" onClick={downloadOcmfFile}>Download signed meter data (OCMF)</button>}
            </section>
            <footer className="receipt-footer"><span>Receipt generated by CPMS</span><span>Aeon Innovative Solutions</span></footer>
          </>
        )}
      </article>
    </main>
  );
};

export default Receipt;
